/**
 * PWA service worker registration + install-prompt helper.
 * Safe to call multiple times — `register` is idempotent.
 */

let deferredPrompt = null;
let listeners = new Set();
let _listenersBound = false;

export function getInstallPromptAvailable() {
  return !!deferredPrompt;
}

export function onInstallPromptChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function notifyListeners() {
  listeners.forEach((cb) => {
    try { cb(!!deferredPrompt); } catch (_) { /* ignore */ }
  });
}

export async function triggerInstall() {
  if (!deferredPrompt) return { outcome: 'not-available' };
  const ev = deferredPrompt;
  deferredPrompt = null;
  notifyListeners();
  ev.prompt();
  const choice = await ev.userChoice;
  return choice;
}

export function isAppInstalled() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  // We deliberately do NOT register /service-worker.js anymore — it conflicted
  // with /firebase-messaging-sw.js at the same scope `/` and caused FCM token
  // subscription to fail (`messaging/token-subscribe-failed: body stream already read`).
  // The Firebase Messaging SW is registered on demand by enablePushNotifications().
  //
  // We still proactively unregister any stale `/service-worker.js` that older
  // builds may have left behind on the device.
  navigator.serviceWorker.getRegistrations()
    .then((regs) => regs.forEach((r) => {
      try {
        const url = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || '';
        if (url.endsWith('/service-worker.js')) r.unregister();
      } catch (_) { /* ignore */ }
    }))
    .catch(() => { /* ignore */ });

  if (process.env.NODE_ENV !== 'production') return;

  if (!_listenersBound) {
    _listenersBound = true;
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      deferredPrompt = e;
      notifyListeners();
    });

    window.addEventListener('appinstalled', () => {
      deferredPrompt = null;
      notifyListeners();
    });
  }
}

/** Force-clean every service worker (used by the "Reparar notificações" button)
 * so the next call to enablePushNotifications gets a fresh FCM registration. */
export async function resetAllServiceWorkers() {
  if (!('serviceWorker' in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
  } catch (_) { /* ignore */ }
  // Also clear any FCM caches the browser may have created
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  } catch (_) { /* ignore */ }
  // Clear Firebase IndexedDB state (installations/messaging). A blocked or failed
  // installation gets persisted here and poisons every subsequent getToken call.
  try {
    const dbs = [
      'firebase-installations-database',
      'firebase-messaging-database',
      'firebase-heartbeat-database',
      'fcm_token_details_db',
    ];
    await Promise.all(dbs.map((name) => new Promise((resolve) => {
      try {
        const req = indexedDB.deleteDatabase(name);
        req.onsuccess = req.onerror = req.onblocked = () => resolve();
      } catch (_) { resolve(); }
    })));
  } catch (_) { /* ignore */ }
}

/** Unregister the main app service worker on logout. Leaves the FCM SW alone
 * (it carries the device token; ripping it out forces token re-issuance). */
export async function unregisterServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  deferredPrompt = null;
  notifyListeners();
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => {
      try {
        const url = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || '';
        if (url.endsWith('/service-worker.js')) return r.unregister().catch(() => false);
      } catch (_) { /* ignore */ }
      return Promise.resolve(false);
    }));
  } catch (_) { /* ignore */ }
}
